import { useLayoutEffect, type RefObject } from "react"
import { micFillLevel, smoothMicFill } from "@/lib/voice-level"

/**
 * Пишет `--mic-fill` (0…1) на элемент, пока жив поток записи.
 * Кадр обновляет стиль напрямую: панель агента из-за метра не перерисовывается.
 * Узел анализатора не подключён к выходу — микрофон в динамики не попадает.
 */
export function useMicLevel(stream: MediaStream | null, targetRef: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = targetRef.current
    if (!stream || !el || typeof AudioContext === "undefined") return

    // До первого кадра переменная уже 0: иначе clip-path без значения показал бы полную иконку.
    // В style prop её не кладём — рекордер перерисовывается по таймеру и затёр бы заливку.
    el.style.setProperty("--mic-fill", "0")

    let ctx: AudioContext
    try {
      ctx = new AudioContext()
    } catch {
      return
    }

    let source: MediaStreamAudioSourceNode
    try {
      source = ctx.createMediaStreamSource(stream)
    } catch {
      void ctx.close()
      return
    }

    const analyser = ctx.createAnalyser()
    analyser.fftSize = 256
    // Сглаживает сам хук: быстрый подъём и медленный спад. Встроенное усреднение
    // анализатора сделало бы заливку вялой поверх этого.
    analyser.smoothingTimeConstant = 0
    source.connect(analyser)

    const samples = new Uint8Array(analyser.fftSize)
    let smoothed = 0
    let raf = 0
    let closed = false

    const tick = () => {
      if (closed) return
      analyser.getByteTimeDomainData(samples)
      smoothed = smoothMicFill(smoothed, micFillLevel(samples))
      el.style.setProperty("--mic-fill", smoothed.toFixed(3))
      raf = requestAnimationFrame(tick)
    }

    void ctx.resume()
    raf = requestAnimationFrame(tick)

    return () => {
      closed = true
      cancelAnimationFrame(raf)
      source.disconnect()
      void ctx.close()
      el.style.removeProperty("--mic-fill")
    }
  }, [stream, targetRef])
}
